// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * Provider Email Override API
 *
 * Manages brokerage-specific email overrides for provider notifications.
 * Implements 4-level email resolution hierarchy:
 * 1. Brokerage-Provider-PolicyType Override (HIGHEST)
 * 2. Brokerage-Provider Override
 * 3. PolicyType Global Override
 * 4. Provider Default Email (LOWEST)
 */

export interface ProviderEmailOverride {
  id?: string; // UUID
  brokerageId: string; // UUID
  providerId: string; // UUID
  policyTypeId?: string | null; // UUID - null = applies to all policy types
  overrideEmail: string;
  emailOverrideReason?: string;

  // Read-only display fields
  providerName?: string;
  policyTypeName?: string;
  brokerageName?: string;

  // Audit fields
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string;
  updatedAt?: string;
}

export interface ProviderEmailOverrideFormData {
  brokerageId: string;
  providerId: string;
  policyTypeId: string | null;
  overrideEmail: string;
  emailOverrideReason: string;
}

export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
  first: boolean;
  last: boolean;
}

const API_BASE = '/api/provider-email-overrides';

export const providerEmailOverridesApi = {
  /**
   * Get all overrides for a specific brokerage
   */
  async getByBrokerage(brokerageId: string): Promise<ProviderEmailOverride[]> {
    const response = await fetch(`${API_BASE}/brokerage/${brokerageId}`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch provider email overrides');
    }
    return response.json();
  },

  /**
   * Get overrides for a brokerage with pagination
   */
  async getByBrokeragePaged(
    brokerageId: string,
    params?: { page?: number; size?: number }
  ): Promise<PageResponse<ProviderEmailOverride>> {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', String(params.page));
    if (params?.size !== undefined) queryParams.append('size', String(params.size));

    const url = `${API_BASE}/brokerage/${brokerageId}/paged${
      queryParams.toString() ? `?${queryParams}` : ''
    }`;
    const response = await fetch(url, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch provider email overrides');
    }
    return response.json();
  },

  /**
   * Get provider-level overrides for a brokerage (applies to all policy types)
   */
  async getProviderLevelOverrides(brokerageId: string): Promise<ProviderEmailOverride[]> {
    const response = await fetch(`${API_BASE}/brokerage/${brokerageId}/provider-level`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch provider-level overrides');
    }
    return response.json();
  },

  /**
   * Get policy-type-level overrides for a brokerage (specific policy types)
   */
  async getPolicyTypeLevelOverrides(brokerageId: string): Promise<ProviderEmailOverride[]> {
    const response = await fetch(`${API_BASE}/brokerage/${brokerageId}/policy-type-level`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch policy-type-level overrides');
    }
    return response.json();
  },

  /**
   * Get override by ID
   */
  async getById(id: string): Promise<ProviderEmailOverride> {
    const response = await fetch(`${API_BASE}/${id}`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch provider email override');
    }
    return response.json();
  },

  /**
   * Create a new provider email override
   */
  async create(data: ProviderEmailOverrideFormData): Promise<ProviderEmailOverride> {
    const response = await fetch(API_BASE, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Failed to create provider email override');
    }
    return response.json();
  },

  /**
   * Update an existing provider email override
   */
  async update(id: string, data: ProviderEmailOverrideFormData): Promise<ProviderEmailOverride> {
    const response = await fetch(`${API_BASE}/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Failed to update provider email override');
    }
    return response.json();
  },

  /**
   * Delete a provider email override
   */
  async delete(id: string): Promise<void> {
    const response = await fetch(`${API_BASE}/${id}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to delete provider email override');
    }
  },

  /**
   * Test email resolution for a specific combination
   * Returns the resolved email address based on 4-level hierarchy
   */
  async resolveEmail(
    brokerageId: string,
    providerId: string,
    policyTypeId?: string
  ): Promise<string> {
    const queryParams = new URLSearchParams();
    queryParams.append('brokerageId', brokerageId);
    queryParams.append('providerId', providerId);
    if (policyTypeId) {
      queryParams.append('policyTypeId', policyTypeId);
    }

    const response = await fetch(`${API_BASE}/resolve-email?${queryParams}`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to resolve email');
    }
    return response.text();
  }
};
